/**
 * iter651 — Foodics-style Printers Settings Page.
 *
 * Clean, full-page printer management. Replaces the need to dive into
 * the POS dialog when a branch manager wants to add or edit printers.
 *
 * Layout (Foodics aesthetic):
 *  • Full-width page header with title + primary CTA on the right
 *  • Filter chips (All / Cashier / Kitchen / Bar / Label) + branch picker
 *  • Card table list (name, branch, ip:port, type, mode, default, status)
 *  • Right-side Sheet drawer for Add/Edit with grouped sections:
 *      1. Identity (name, role, station, branch)
 *      2. Network (ip, port, paper width, test)
 *      3. Behaviour (print mode, copies, default, auto-print)
 *  • Empty state, inline status pings, Test print + Delete actions
 *
 * Reuses the existing /api/cashier/printers endpoints — no backend
 * changes required. The original POS dialog stays in place for the
 * cashier kiosk; this page is for the manager view.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Printer, Plus, Edit, Wifi, Loader2, CheckCircle2, XCircle,
  ArrowLeft, Info, ExternalLink, Upload, Radar, Zap, Settings as SettingsIcon,
  ChevronDown, AlertTriangle, HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { isBridgeAlive, pingPrinter, isLikelyIOS, discoverPrinters, printViaAirPrint, identifyPrinter, verifyAirPrint, enableAirPrint } from '@/lib/printBridge';
import { isCapacitorNative, isAndroid } from '@/lib/platformDetect';
import PrinterHelpDrawer from '@/components/printers-settings/PrinterHelpDrawer';
import { printViaEposDirect, openPrinterCertPage } from '@/lib/eposPrint';
import { ReceiptPreviewDialog } from '@/components/pos/ReceiptPreview';
import PosTerminalBranchDialog from '@/components/pos/PosTerminalBranchDialog';
import IPadOptionsDialog from '@/components/printers-settings/IPadOptionsDialog';
import PrinterEditDrawer from '@/components/printers-settings/PrinterEditDrawer';
import EposDirectPanel from '@/components/printers-settings/EposDirectPanel';
import PrinterListView from '@/components/printers-settings/PrinterListView';
import { PRINTER_MODELS, findModelById, findModelByIdentity } from '@/lib/printerModels';
import { QRCodeSVG } from 'qrcode.react';

const EMPTY_FORM = {
  name: '',
  type: 'receipt',
  station: '',
  ip_address: '',
  port: 9100,
  paper_width: '80mm',
  is_default: false,
  auto_print: false,
  copies: 1,
  branch_id: null,
  connection_type: 'network',
  device_address: '',
  print_mode: 'auto',
  airprint_enabled: false,
  // iter890 — Every new Epson TM printer defaults to `local_printer`
  // because that's the ePOS-Print factory Device ID for 99% of tenants.
  // Save-time guard still lets power-users override.
  epos_device_id: 'local_printer',
  // iter843 — Native USB (Electron Desktop) identifiers. Set when the
  // printer is plugged directly into a Windows/Mac/Linux running the
  // SmartoERP Desktop app. Written as decimal ints (VID 1208 = 0x04b8).
  usb_vendor_id: '',
  usb_product_id: '',
};

// iter696 — TYPE_META, ROLE_FILTERS, and relTime moved INTO
// <PrinterListView> since they were only used inside the table view.

export default function PrintersSettingsPage() {
  const [printers, setPrinters] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter696 — search / roleFilter / branchFilter state moved INTO
  // <PrinterListView> since only the list view reads them.
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [pinging, setPinging] = useState(null);
  const [pingResults, setPingResults] = useState({});
  const iOSMode = isLikelyIOS();
  // iter841 — Android *browser* detection (i.e., Chrome/Samsung Internet
  // on an Android tab NOT inside our native SmartoERP app). USB and
  // Bluetooth printer discovery only work inside the native Capacitor
  // build; when the user is on the mobile web we surface a persistent
  // hint so they don't waste time troubleshooting a USB cable.
  const androidBrowser = isAndroid() && !isCapacitorNative();
  // iter870 — Detect "inside Android APK but native SmartoPrinter plugin is
  // missing" — almost always means the user is running an OLD APK built
  // before the DantSu USB bridge was included. Sync-checkable because
  // Capacitor.Plugins is set synchronously on webview load.
  const androidAppOldApk = (() => {
    try {
      if (!(isCapacitorNative() && isAndroid())) return false;
      const p = window.Capacitor?.Plugins?.SmartoPrinter;
      return !p;
    } catch { return false; }
  })();
  const [bridgeAlive, setBridgeAlive] = useState(iOSMode ? 'ipad' : null);
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return sessionStorage.getItem('printers_bridge_banner_dismissed') === '1'; }
    catch { return false; }
  });
  // iter844 — Printer troubleshooting help drawer.
  const [helpOpen, setHelpOpen] = useState(false);
  // iter652 — Bulk-import dialog state.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCsv, setBulkCsv] = useState('');
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  // iter653 — Scan LAN dialog state. Only the open flag stays in the
  // parent because the trigger button in the Setup dropdown needs to
  // flip it. All discovery results / iPad-quick-add / AirPrint-fix
  // state was moved inside <EposDirectPanel> in iter695.
  const [scanOpen, setScanOpen] = useState(false);
  // iter871 — Auto-discover: unified "Scan Wi-Fi" button next to Add printer.
  // Tries the native Android Capacitor plugin first (works over the tablet's
  // Wi-Fi), then falls back to the desktop Print Bridge /discover endpoint,
  // then opens the manual entry dialog as a last resort.
  const [autoDiscoverBusy, setAutoDiscoverBusy] = useState(false);
  const [autoDiscoverResults, setAutoDiscoverResults] = useState(null);
  const [autoDiscoverOpen, setAutoDiscoverOpen] = useState(false);
  // iter662 — AirPrint verifier state. `result` is the bridge response;
  // when null we show the trigger button, otherwise we render the result
  // pill (green/red/amber). SHARED between EposDirectPanel and
  // IPadOptionsDialog (the iPrint toggle reads it for a re-render bump).
  const [ipadVerifyState, setIpadVerifyState] = useState({ loading: false, result: null, error: null });
  // iter664 — Android (Termux) Bridge install help dialog state.
  const [androidHelpOpen, setAndroidHelpOpen] = useState(false);
  const [relayToken, setRelayToken] = useState(null);
  const [relayTokenLoading, setRelayTokenLoading] = useState(false);
  // iter874 — capture the real error so users see WHY the fetch failed
  // (was: silent .catch swallowed everything → "unable to load" with no clue).
  const [relayTokenError, setRelayTokenError] = useState('');
  const loadRelayToken = () => {
    setRelayTokenLoading(true);
    setRelayTokenError('');
    api.get('/cashier/print-bridge/relay-token')
      .then((res) => setRelayToken(res.data?.relay_token || ''))
      .catch((err) => {
        setRelayToken('');
        setRelayTokenError(
          err?.response?.status
            ? `HTTP ${err.response.status} · ${err.response.data?.detail || err.message}`
            : (err?.message || 'network error'),
        );
      })
      .finally(() => setRelayTokenLoading(false));
  };
  // iter665 — Test Bridge: poll /bridges and report which (if any) is online
  //          right now. Helps the cashier confirm Termux is actually working
  //          before they wander off.
  const [bridgeTest, setBridgeTest] = useState({ loading: false, result: null });
  // iter669 — ePOS-Print Direct test state.
  const [eposTestState, setEposTestState] = useState({ loading: false, result: null, error: null });
  // iter679 — Per-row ePOS Direct test. Tracks which printer row is
  // currently being fired so its button can show a spinner.
  // iter680 — Per-row ePOS Direct test spinner id.
  const [eposRowTesting, setEposRowTesting] = useState(null);
  // iter915 — Per-row bilingual (EN + AR) test print spinner id.
  const [bilingualTesting, setBilingualTesting] = useState(null);
  // iter679 — Bundled "iPad printing options" dialog. Replaces the two
  // big inline iOS-only cards (auto-iPrint + ePOS-Direct setup) which
  // were making the page noisy. Click the chip in the header to open.
  const [ipadOptionsOpen, setIpadOptionsOpen] = useState(false);
  // iter683 — Receipt preview dialog. Renders a pixel-accurate mock
  // of the actual thermal receipt so the operator can verify their
  // header/footer/logo without burning paper.
  const [previewOpen, setPreviewOpen] = useState(false);
  // iter690 — Per-terminal branch picker. Writes to
  // localStorage.pos_terminal_branch_id which the CashierLoginPage
  // reads on PIN login → server stamps the session with THAT branch.
  const [terminalBranchOpen, setTerminalBranchOpen] = useState(false);
  const _navigate = useNavigate();
  const [_searchParams] = useSearchParams();
  // iter — Terminal setup deep-link from the Cashier/Waiter kiosk "Admin Setup"
  // flow: auto-open the existing terminal-branch dialog, and after a branch is
  // saved (pos_terminal_branch_id present) return to the kiosk screen.
  const _terminalReturnTo = _searchParams.get('returnTo');
  useEffect(() => {
    if (_searchParams.get('terminalSetup') === '1') setTerminalBranchOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const _handleTerminalOpenChange = (o) => {
    setTerminalBranchOpen(o);
    if (!o && _terminalReturnTo) {
      try {
        if (localStorage.getItem('pos_terminal_branch_id')) _navigate(_terminalReturnTo);
      } catch { /* ignore */ }
    }
  };
  // iter670 — Confirmation modal that pops 1.5s after Test ePOS fires.
  // Auto-flips the toggle ON when the cashier confirms the slip printed.
  const [eposConfirmOpen, setEposConfirmOpen] = useState(false);
  // iter672 — Mass QR setup wizard state. Generates one QR code per
  // branch encoding /settings/printers?wizard=epos&printer_ip=... so
  // cashiers can scan from their iPad/Samsung Tab and land on the
  // guided ePOS-Direct setup with the IP pre-filled.
  const [massQrOpen, setMassQrOpen] = useState(false);
  // iter676 — Accept-ALL-certs wizard state. Walks the cashier through
  // accepting the self-signed HTTPS cert on every printer with an IP,
  // one printer at a time (popup blockers prevent batch open).
  const [acceptAllOpen, setAcceptAllOpen] = useState(false);
  const [acceptAllIndex, setAcceptAllIndex] = useState(0);
  // iter657 — Guided Epson TM-m30II setup wizard state.
  const [tmWizardOpen, setTmWizardOpen] = useState(false);
  const [tmWizardStep, setTmWizardStep] = useState(0);
  const [tmWizardForm, setTmWizardForm] = useState({ name: 'Cashier - TM-m30II', ip_address: '', branch_id: null });
  const [tmWizardCreating, setTmWizardCreating] = useState(false);
  // iter658 — On iPad, AirPrint discovery happens in Safari's native print
  // dialog (mDNS/Bonjour) — we don't need an IP at all. Default the IP-required
  // path off when iOS is detected so the wizard collapses to 1 click.
  const [tmWizardMode, setTmWizardMode] = useState(iOSMode ? 'ipad-fast' : 'full');

  const reload = async () => {
    setLoading(true);
    try {
      const [pR, bR, aliveR] = await Promise.allSettled([
        api.get('/cashier/printers'),
        api.get('/branches'),
        iOSMode ? Promise.resolve('ipad') : isBridgeAlive(),
      ]);
      if (pR.status === 'fulfilled') setPrinters(pR.value.data || []);
      if (bR.status === 'fulfilled') setBranches(Array.isArray(bR.value.data) ? bR.value.data : []);
      if (aliveR.status === 'fulfilled') setBridgeAlive(aliveR.value);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // iter672 — Deep-link wizard auto-scroll. When the cashier scans the
  // QR on their tablet, the URL carries `?wizard=epos&printer_ip=X`.
  // We pre-fill the IP into the first applicable printer (or surface
  // a quick-add prompt), scroll the ePOS-Direct panel into view, and
  // pulse-highlight it so the cashier knows where to look.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('wizard') !== 'epos') return;
    setTimeout(() => {
      // iter679 — wizard flow now opens the iPad options dialog instead
      // of relying on the inline panel that no longer exists.
      setIpadOptionsOpen(true);
      const el = document.querySelector('[data-testid="epos-direct-row"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-emerald-400', 'animate-pulse');
        setTimeout(() => el.classList.remove('animate-pulse'), 4500);
      }
      const ip = params.get('printer_ip');
      if (ip) {
        toast.info(`Printer IP from QR: ${ip} — confirm it matches the printer on this branch.`);
      }
    }, 800);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // iter696 — `branchName` helper and `filtered` useMemo moved INTO
  // <PrinterListView> alongside the search/role/branch filter state.

  const openAdd = () => {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setSheetOpen(true);
  };

  // iter871 — Auto-discover network printers.
  //   Path A (Android APK): use the native SmartoPrinter Capacitor plugin's
  //     discoverNetwork() — scans the tablet's /24 subnet for TCP-9100 in ~2s.
  //   Path B (Desktop w/ Print Bridge): fall back to /discover on the local
  //     Bridge (existing behaviour, iter653).
  //   Path C (browser-only, no Bridge): open the manual "Add by IP" dialog
  //     with a helpful hint — the browser can't scan the LAN.
  const handleAutoDiscover = async () => {
    setAutoDiscoverBusy(true);
    setAutoDiscoverResults(null);
    try {
      // Path A — native Android plugin
      if (isCapacitorNative() && isAndroid()) {
        try {
          const { discoverNetworkPrinters } = await import('@/lib/nativeThermalPrint');
          const res = await discoverNetworkPrinters(9100);
          if (res.available) {
            const printers = res.printers || [];
            setAutoDiscoverResults({ source: 'android_plugin', printers, subnet: res.subnet, error: res.error });
            setAutoDiscoverOpen(true);
            if (printers.length === 0) {
              toast.info(res.error ? `Scan finished — ${res.error}` : `No ESC/POS printers found on ${res.subnet || 'your Wi-Fi'} (port 9100). Ensure printers are powered on and on the same Wi-Fi.`);
            } else {
              toast.success(`Found ${printers.length} printer${printers.length === 1 ? '' : 's'} on your Wi-Fi`);
            }
            return;
          }
        } catch (e) { /* fall through */ }
      }
      // Path B — Print Bridge on the LAN (desktop)
      const bridgeRes = await discoverPrinters();
      if (bridgeRes?.ok) {
        const printers = bridgeRes.data?.printers || [];
        setAutoDiscoverResults({ source: 'print_bridge', printers });
        setAutoDiscoverOpen(true);
        if (printers.length === 0) {
          toast.info('Print Bridge scan returned no printers. Check that printers are powered on and reachable on TCP-9100.');
        } else {
          toast.success(`Found ${printers.length} printer${printers.length === 1 ? '' : 's'} via Print Bridge`);
        }
        return;
      }
      // Path C — no discovery available. Open manual scan dialog as a
      // graceful fallback so the user can still add a printer by IP.
      setScanOpen(true);
      toast.info("Automatic scan needs the SmartoERP Android app OR the Print Bridge on a nearby PC. Meanwhile, add the printer by IP below.");
    } catch (e) {
      toast.error(`Auto-discover failed: ${e?.message || e}`);
    } finally {
      setAutoDiscoverBusy(false);
    }
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '',
      type: p.type || 'receipt',
      station: p.station || '',
      ip_address: p.ip_address || '',
      port: p.port || 9100,
      paper_width: p.paper_width || '80mm',
      is_default: !!p.is_default,
      auto_print: !!p.auto_print,
      copies: p.copies || 1,
      branch_id: p.branch_id || null,
      connection_type: p.connection_type || (p.airprint_enabled ? 'airprint' : 'network'),
      device_address: p.device_address || '',
      print_mode: p.print_mode || 'auto',
      airprint_enabled: !!p.airprint_enabled,
      // iter890 — Backfill `local_printer` for any pre-iter890 printer
      // that was saved with an empty devid.
      epos_device_id: p.epos_device_id || 'local_printer',
      usb_vendor_id: p.usb_vendor_id != null ? String(p.usb_vendor_id) : '',
      usb_product_id: p.usb_product_id != null ? String(p.usb_product_id) : '',
    });
    setSheetOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Printer name is required'); return; }

    // iter875 — Guard against the #1 mis-configuration seen in production
    // print logs: users save the printer's IP address as the ePOS Device ID.
    // Every job then gets rejected by Epson with `code=DeviceNotFound` even
    // though HTTP is 200. Prompt before saving an IP-shaped devid.
    const devid = (form.epos_device_id || '').trim();
    const looksLikeIp = /^(\d{1,3}\.){3}\d{1,3}$/.test(devid);
    if (looksLikeIp) {
      const ok = window.confirm(
        `⚠️ The ePOS Device ID "${devid}" looks like an IP address.\n\n` +
        `99% of Epson TM printers use the literal string "local_printer" (not the IP). ` +
        `Saving the IP will make the printer silently reject every job with code=DeviceNotFound.\n\n` +
        `• OK → keep "${devid}" (only correct if you customized WebConfig → ePOS-Print → Device ID)\n` +
        `• Cancel → I'll set it back to "local_printer" for you\n`
      );
      if (!ok) {
        setForm({ ...form, epos_device_id: 'local_printer' });
        toast.info('Device ID reset to "local_printer". Tap Save again.');
        return;
      }
    }

    setSaving(true);
    try {
      // iter843 — Cast VID/PID to ints where present so the backend
      // stores them as numeric (Electron IPC expects ints).
      // iter890 — Normalise connection_type + epos_device_id at save
      // time so USB choice is never silently downgraded to "network"
      // and every printer ships with a working ePOS Device ID.
      // iter904 — Belt-and-suspenders: if the form.connection_type is
      // still falsy at save time (never happens via the picker but has
      // been observed on hand-crafted rows / older imports), fall back
      // to "network" when an IP is present, "usb" when the form has a
      // USB VID/PID, and "network" as an ultimate default. Prevents
      // the print pipeline from skipping the raw-9100 branch just
      // because the value was blank.
      let _rawCt = (form.connection_type || '').toLowerCase();
      if (!_rawCt) {
        if (form.usb_vendor_id || form.usb_product_id) _rawCt = 'usb';
        else if (form.airprint_enabled) _rawCt = 'airprint';
        else _rawCt = 'network';
      }
      const normalizedConnType = ['network', 'usb', 'bluetooth', 'sunmi', 'airprint', 'cloud_relay'].includes(_rawCt)
        ? _rawCt
        : (form.airprint_enabled ? 'airprint' : 'network');
      const payload = {
        ...form,
        connection_type: normalizedConnType,
        epos_device_id: (form.epos_device_id || '').trim() || 'local_printer',
        usb_vendor_id: form.usb_vendor_id === '' ? null : Number(form.usb_vendor_id),
        usb_product_id: form.usb_product_id === '' ? null : Number(form.usb_product_id),
      };
      if (editing) {
        await api.put(`/cashier/printers/${editing.id}`, payload);
        toast.success('Printer updated');
      } else {
        await api.post('/cashier/printers', payload);
        toast.success('Printer added');
      }
      setSheetOpen(false);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/cashier/printers/${p.id}`);
      toast.success('Printer deleted');
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const ping = async (p) => {
    if (!p.ip_address) { toast.error('No IP address configured'); return; }
    if (iOSMode) {
      toast.info('Ping is a local-LAN test. On iPad, send a real Test print — routes via Cloud Relay.');
      return;
    }
    if (!bridgeAlive) {
      toast.error('Print Bridge not running. Visit /printer-setup to install.');
      return;
    }
    setPinging(p.id);
    const r = await pingPrinter(p.ip_address, p.port || 9100);
    setPinging(null);
    setPingResults(m => ({ ...m, [p.id]: r.ok ? 'ok' : 'fail' }));
    if (r.ok) toast.success(`${p.name} reachable`);
    else toast.error(`${p.name} not reachable`);
  };

  const testPrint = async (p) => {
    setTesting(p.id);
    try {
      // Native Android app: Bluetooth/USB printers print via the ESC/POS
      // plugin. (Network/AirPrint/Windows/browser are untouched below.)
      if ((p.connection_type === 'bluetooth' || p.connection_type === 'usb' || p.connection_type === 'sunmi')) {
        const { isNativeThermalAvailable, printNativeThermal } = await import('@/lib/nativeThermalPrint');
        if (isNativeThermalAvailable()) {
          const { data } = await api.post(`/cashier/printers/${p.id}/test`);
          const r = await printNativeThermal(p, data.test_payload || {});
          if (r.ok) toast.success(`Test sent to ${p.name} via ${p.connection_type.toUpperCase()}`);
          else toast.error(r.error || 'Native print failed');
          await reload();
          return;
        }
        toast.info('Bluetooth/USB/Sunmi printing works inside the SmartoERP Android app. On this device, configure a Network or AirPrint printer instead.');
        setTesting(null);
        return;
      }
      // iter656 — If the printer is marked AirPrint and we're on iPad,
      // print directly via Safari's print dialog. No Bridge required.
      if (p.airprint_enabled && iOSMode) {
        const { data } = await api.post(`/cashier/printers/${p.id}/test`);
        const { printViaAirPrint } = await import('@/lib/printBridge');
        printViaAirPrint(p, data.test_payload);
        toast.success(`Opening AirPrint dialog — pick "${p.name}" from the list`);
        await reload();
        return;
      }
      await api.post(`/cashier/printers/${p.id}/test`);
      toast.success(`Test ticket sent to ${p.name}`);
      // iter654 — pick up the new last_tested_at stamp from the server.
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test print failed');
    } finally {
      setTesting(null);
    }
  };

  /**
   * iter680 — Fire an ePOS-Print Direct test at EVERY printer that has
   * an IP address. Sequential (not parallel) so the operator can hear
   * each printer fire in order and trace which one is silent. Updates
   * `eposRowTesting` so the row's button shows a spinner during its
   * turn. Final toast summarises X/Y reached the network.
   */
  const testEposDirectAll = async () => {
    const targets = printers.filter(p => p.ip_address);
    if (targets.length === 0) {
      toast.error('No printers have an IP address configured.');
      return;
    }
    if (!window.confirm(`Fire an ePOS Direct test to all ${targets.length} printer(s) sequentially?\n\nEach printer should print a small "TEST" slip. If any are silent, check that printer's Device ID setting.`)) return;
    const runId = toast.loading(`Testing ${targets.length} printer(s)…`);
    let okCount = 0;
    const failed = [];
    for (const p of targets) {
      setEposRowTesting(p.id);
      const sample = {
        company: 'Smarto',
        title: `ePOS DIRECT TEST — ${p.name || ''}`,
        order_number: 'TEST-ALL-' + Date.now().toString().slice(-4),
        date: new Date().toLocaleString(),
        items: [{ qty: 1, name: 'ePOS Direct probe', subtotal: 0 }],
        subtotal: 0, tax: 0, total: 0, currency: '',
        footer: 'If you can read this, ePOS Direct is working for this printer.',
      };
      try {
        const r = await printViaEposDirect(p, sample);
        if (r.ok) okCount += 1;
        else failed.push(`${p.name} (${r.error || 'err'})`);
      } catch {
        failed.push(`${p.name} (exception)`);
      }
      // small spacing so the printers don't get a thundering herd.
      await new Promise(res => setTimeout(res, 300));
    }
    setEposRowTesting(null);
    if (failed.length === 0) {
      toast.success(`All ${okCount}/${targets.length} printers received the test. Check the paper at each device!`, { id: runId });
    } else {
      toast.error(`${okCount}/${targets.length} reached the network. Failed: ${failed.join(', ').slice(0, 200)}`, { id: runId });
    }
  };

  /**
   * iter679 — Fire an ePOS-Print Direct test at a single printer row.
   * Bypasses the Print Bridge entirely (so it works on iPads without
   * a PC at the branch) and shows a toast with the exact IP + devid
   * that was used, so the operator can verify their Device ID setting
   * is being picked up correctly.
   */
  const testEposDirectRow = async (p) => {
    if (!p?.ip_address) {
      toast.error(`${p?.name || 'Printer'} has no IP address`);
      return;
    }
    setEposRowTesting(p.id);
    // iter679 — Show an immediate "fired" toast so the operator sees
    // their tap registered (fetch is no-cors so we never get a real
    // response). The success toast that follows will overwrite this.
    const sendingId = toast.loading(`Sending ePOS Direct test to ${p.name}…`);
    try {
      const sample = {
        company: 'Smarto',
        title: `ePOS DIRECT TEST — ${p.name || ''}`,
        order_number: 'TEST-' + Date.now().toString().slice(-4),
        date: new Date().toLocaleString(),
        items: [{ qty: 1, name: 'ePOS Direct probe', subtotal: 0 }],
        subtotal: 0, tax: 0, total: 0, currency: '',
        footer: 'If this prints, ePOS Direct is working for this printer.',
      };
      const r = await printViaEposDirect(p, sample);
      if (r.ok) {
        toast.success(`${p.name} → fired to ${r.ip} (devid=${r.deviceId}). Check the paper!`, { id: sendingId });
      } else {
        toast.error(r.msg || `${p.name}: ePOS test failed`, { id: sendingId });
      }
    } finally {
      setEposRowTesting(null);
    }
  };

  // iter915 — Send a bilingual (English + Arabic) test receipt to this
  // printer. Exercises the exact same ePOS-Direct raster path used by
  // real orders — so a cashier can visually confirm that (a) the
  // Company / Restaurant / Branch header shows up, (b) the logo lands
  // on paper (no SecurityError / no missing image), and (c) Arabic
  // shapes correctly instead of the old text-mode garbling. Includes
  // English AND Arabic item names, footer, and language flag =
  // 'both' so labels print side-by-side. Also includes a fake ZATCA
  // seller name so the raster path renders the QR too.
  const testBilingualRow = async (p) => {
    if (!p?.ip_address) {
      toast.error(`${p?.name || 'Printer'} has no IP address`);
      return;
    }
    setBilingualTesting(p.id);
    const sendingId = toast.loading(`Sending bilingual test to ${p.name}…`);
    try {
      // Try to pull the tenant's branding + branch so the test uses
      // the REAL logo / company / VAT — not a stub — proving the
      // production path works end-to-end.
      let branding = null;
      let branchName = '';
      try {
        const { data: br } = await api.get('/pdf-exports/branding');
        branding = br || null;
      } catch { /* branding optional */ }
      try {
        const { data: brs } = await api.get('/branches');
        const list = Array.isArray(brs) ? brs : (brs?.items || []);
        const branch = list.find((b) => b.id === p.branch_id) || list[0];
        branchName = branch?.name || '';
      } catch { /* branch name optional */ }
      // iter915 — Pre-convert the logo to a Data URI so the raster
      // path never trips a cross-origin SecurityError.
      let logoDataUri = '';
      try {
        const rawLogo = (branding && (branding.thermal_logo_url || branding.logo_url)) || '';
        if (rawLogo) {
          const abs = /^https?:\/\//i.test(rawLogo) || rawLogo.startsWith('data:')
            ? rawLogo
            : `${process.env.REACT_APP_BACKEND_URL || ''}${rawLogo.startsWith('/uploads/') ? '/api' : ''}${rawLogo.startsWith('/') ? '' : '/'}${rawLogo}`;
          const { logoUrlToDataUri } = await import('@/lib/thermalLogoOptimizer');
          const paperMm = parseInt(String(p.paper_width || '80').replace('mm', ''), 10) || 80;
          logoDataUri = await logoUrlToDataUri(abs, paperMm);
        }
      } catch { /* logo optional */ }
      const sample = {
        title: 'BILINGUAL TEST — اختبار',
        company: branding?.company_name || 'Smarto',
        branch: branchName || 'Test Branch',
        vat: branding?.company_vat || '300000000000003',
        order_number: 'BILI-' + Date.now().toString().slice(-4),
        check_number: 'BILI-' + Date.now().toString().slice(-4),
        date: new Date().toISOString(),
        order_type: 'Dine-in',
        server: 'Test Cashier',
        items: [
          { qty: 1, name: 'Espresso · إسبريسو', subtotal: 12.00, modifiers: [{ name: 'إضافة سكر' }] },
          { qty: 2, name: 'شاورما دجاج · Chicken Shawarma', subtotal: 40.00 },
          { qty: 1, name: 'Water · ماء', subtotal: 3.00, note: 'بدون ثلج / no ice' },
        ],
        subtotal: 55.00,
        discount: 0,
        tax: 8.25,
        tax_pct: 15,
        total: 63.25,
        currency: 'SAR',
        payment_method: 'CASH',
        payment_label: 'Cash · نقدي',
        header: branding?.receipt_header_text || 'مطعم اختبار · Test Restaurant',
        footer: (branding?.footer_text || '') + '\nThank you for your visit · شكرا لزيارتكم',
        logo_url: logoDataUri || '',
        logo_scale: 1,
        zatca_seller_name: branding?.zatca_seller_name || branding?.company_name || 'Smarto',
        options: { show_arabic_header: true, show_zatca_qr: true, show_company_at_bottom: true },
        print_language: 'both',
        main_language: 'en',
        localized_language: 'ar',
      };
      const r = await printViaEposDirect(p, sample);
      // iter917 — Persist the outcome so the trend strip on the printer
      // row can surface it. Silently swallow failures (log is nice-to-
      // have, not critical to the actual print).
      try {
        await api.post(`/cashier/printers/${p.id}/test-log`, {
          kind: 'bilingual',
          ok: !!r.ok,
          mode: r.mode || '',
          msg: r.msg || '',
          logo: r.logo || '',
        });
        // Refresh the row's trend strip.
        await loadBilingualTrend(p.id);
      } catch { /* trend logging is best-effort */ }
      if (r.ok) {
        toast.success(
          `${p.name} → bilingual test fired to ${r.ip}. Check the paper: logo, Restaurant Name, Company, Arabic item names.`,
          { id: sendingId },
        );
      } else {
        toast.error(r.msg || `${p.name}: bilingual test failed`, { id: sendingId });
      }
    } catch (e) {
      toast.error(e?.message || 'Bilingual test failed', { id: sendingId });
    } finally {
      setBilingualTesting(null);
    }
  };

  // iter917 — Bilingual Test Trend: fetch the last 5 bilingual test
  // outcomes per printer + expose to <PrinterListView> as a strip of
  // colored dots (green = ok, red = failed) with hover-tooltips.
  const [bilingualTrends, setBilingualTrends] = useState({});
  const loadBilingualTrend = async (printerId) => {
    try {
      const { data } = await api.get(`/cashier/printers/${printerId}/test-log`, {
        params: { kind: 'bilingual', limit: 5 },
      });
      setBilingualTrends((prev) => ({ ...prev, [printerId]: data?.entries || [] }));
    } catch { /* optional */ }
  };
  // Auto-load on mount for every printer with an IP.
  useEffect(() => {
    (async () => {
      for (const p of printers || []) {
        if (p.ip_address) {
          // Fire in parallel — small tenants have <10 printers.
          loadBilingualTrend(p.id);
        }
      }
    })();
  }, [printers.length]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6 relative" data-testid="printers-settings-page">
        {/* iter656 — Subtle ambient glow + grain texture behind the cards
            for a Foodics-meets-Linear aesthetic. CSS-only, no extra layout. */}
        <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
          <div className="absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-orange-200/30 blur-3xl" />
          <div className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full bg-sky-200/25 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.03] mix-blend-multiply" style={{ backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\'><filter id=\'n\'><feTurbulence baseFrequency=\'0.9\' /></filter><rect width=\'100%\' height=\'100%\' filter=\'url(%23n)\' opacity=\'0.5\'/></svg>")' }} />
        </div>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
              <Link to="/settings" className="hover:underline flex items-center gap-1" data-testid="back-to-settings">
                <ArrowLeft size={12} /> Settings
              </Link>
              <span>/</span>
              <span>Printers</span>
            </div>
            <h1 className="text-2xl font-bold font-outfit dark:text-white" data-testid="printers-page-title">Printers</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Configure thermal receipt, kitchen, bar and label printers. Per branch, per role.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* iter679 — iPad printing options chip (only shown on iPads).
                Replaces the two huge inline cards below the header that
                used to push the printer table 250px down the page. */}
            {iOSMode && (
              <Button
                variant="outline"
                size="sm"
                className="h-9 rounded-lg border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                onClick={() => setIpadOptionsOpen(true)}
                data-testid="ipad-options-btn"
                title="ePOS-Print Direct, Auto-iPrint, and per-iPad debug settings"
              >
                <span className="mr-1.5 text-[14px]">📱</span>
                iPad options
              </Button>
            )}
            {/* iter679 — Single Setup dropdown collapses the 5 secondary
                buttons (Setup guide, TM-m30II wizard, QR rollout, bulk
                auto-print, Scan LAN, Bulk import) into one menu so the
                header stays focused on the primary action. */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 rounded-lg"
                  data-testid="setup-menu-btn"
                >
                  <SettingsIcon size={14} className="mr-1.5" />
                  Setup
                  <ChevronDown size={12} className="ml-1 opacity-60" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Guided wizards</DropdownMenuLabel>
                <DropdownMenuItem asChild data-testid="setup-menu-open-setup-guide">
                  <Link to="/printer-setup" className="flex items-center gap-2 cursor-pointer">
                    <ExternalLink size={13} />
                    <span>Setup guide</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setTmWizardOpen(true); setTmWizardStep(0); }}
                  data-testid="setup-menu-tm-m30ii"
                >
                  <span className="text-[13px] mr-2">🍎</span>
                  <span>Setup TM-m30II for iPad</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setMassQrOpen(true)}
                  data-testid="setup-menu-mass-qr"
                >
                  <span className="text-[13px] mr-2">📱</span>
                  <span>Setup all branches (QR)</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setPreviewOpen(true)}
                  data-testid="setup-menu-receipt-preview"
                >
                  <span className="text-[13px] mr-2">🧾</span>
                  <span>Preview receipt layout</span>
                </DropdownMenuItem>
                {/* iter690 — Lets an admin pin THIS device (iPad / PC)
                    to a specific branch so cashier PIN logins from
                    here always stamp sales under that branch. */}
                <DropdownMenuItem
                  onClick={() => setTerminalBranchOpen(true)}
                  data-testid="setup-menu-terminal-branch"
                >
                  <span className="text-[13px] mr-2">🏢</span>
                  <span>This device&apos;s branch (POS terminal)</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">Bulk actions</DropdownMenuLabel>
                <DropdownMenuItem
                  disabled={printers.filter(p => p.ip_address).length === 0}
                  onClick={() => testEposDirectAll()}
                  data-testid="setup-menu-test-all-epos"
                >
                  <Zap size={13} className="mr-2 text-emerald-600" />
                  <span>Test ⚡ ePOS on all printers</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={printers.length === 0}
                  onClick={async () => {
                    const allOn = printers.length > 0 && printers.every(p => p.auto_print);
                    const next = !allOn;
                    if (!window.confirm(`${next ? 'Enable' : 'Disable'} auto-print on ALL ${printers.length} printer(s) across every branch?`)) return;
                    try {
                      const { data } = await api.post('/cashier/printers/bulk-set-auto-print', { enabled: next, scope: 'all' });
                      toast.success(`Auto-print ${next ? 'enabled' : 'disabled'} on ${data.updated} printer(s)`);
                      await reload();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to update');
                    }
                  }}
                  data-testid="setup-menu-bulk-auto-print"
                >
                  <Zap size={13} className="mr-2" />
                  <span>{printers.length > 0 && printers.every(p => p.auto_print) ? 'Disable auto-print (all)' : 'Enable auto-print (all)'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setScanOpen(true); }}
                  data-testid="setup-menu-scan-lan"
                >
                  <Radar size={13} className="mr-2" />
                  <span>{iOSMode ? 'Add by IP' : 'Scan LAN'}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => { setBulkOpen(true); setBulkResult(null); }}
                  data-testid="setup-menu-bulk-import"
                >
                  <Upload size={13} className="mr-2" />
                  <span>Bulk import (CSV)</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              variant="outline"
              className="h-9 rounded-lg text-sky-700 border-sky-300 hover:bg-sky-50"
              onClick={() => setHelpOpen(true)}
              data-testid="printer-help-btn"
              title="Printer not working? Open the quick fixes guide"
            >
              <HelpCircle size={14} className="mr-1.5" />
              Help
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-9 rounded-lg border-teal-300 text-teal-700 hover:bg-teal-50"
              onClick={handleAutoDiscover}
              disabled={autoDiscoverBusy}
              data-testid="auto-discover-btn"
              title="Scan your Wi-Fi for ESC/POS printers on port 9100"
            >
              {autoDiscoverBusy
                ? <Loader2 size={14} className="mr-1.5 animate-spin" />
                : <Radar size={14} className="mr-1.5" />}
              Auto-discover
            </Button>
            {/* iter898 — USB Grant + Test button. Visible only inside
                the native Android app (isCapacitorNative + isAndroid).
                Fires the OS permission dialog for every plugged-in
                USB printer + prints a 32-char diagnostic ticket so
                on-boarding a new tablet is a single tap. */}
            {isCapacitorNative() && isAndroid() && (
              <Button
                size="sm"
                variant="outline"
                className="h-9 rounded-lg border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={async () => {
                  const t = toast.loading('Requesting USB permission + running diagnostic print…', { id: 'usb-diag' });
                  const { usbDiagnosticPrint } = await import('@/lib/nativeThermalPrint');
                  const r = await usbDiagnosticPrint();
                  if (r.ok) {
                    toast.success(`Diagnostic sent → ${r.device}. Check the printer.`, { id: 'usb-diag', duration: 6000 });
                  } else {
                    toast.error(r.error, { id: 'usb-diag', duration: 8000 });
                  }
                }}
                data-testid="usb-grant-test-btn"
                title="Grant USB permission + print a diagnostic ticket"
              >
                <span className="mr-1.5">🔌</span>
                Grant USB &amp; Test
              </Button>
            )}
            <Button
              size="sm"
              className="h-9 rounded-lg bg-orange-500 hover:bg-orange-600 text-white"
              onClick={openAdd}
              data-testid="add-printer-btn"
            >
              <Plus size={14} className="mr-1.5" />
              Add new printer
            </Button>
          </div>
        </div>

        {/* iter679 — Bridge status. Way more compact than before.
            Hidden entirely when bridge is healthy (success → silence).
            Slim 1-line chip on iPad or when bridge needs installing. */}
        {/* iter841 — Android BROWSER USB warning. Explicit + actionable.
            Only shown when the user is on Android mobile web (not the
            native app). Web browsers cannot access USB printers due to
            OS-level restrictions — the only way is the native SmartoERP
            Android app. */}
        {androidBrowser && (
          <div
            className="flex items-start justify-between gap-2 rounded-lg px-3 py-2 text-[12px] border bg-amber-50 text-amber-900 border-amber-300 mb-2"
            data-testid="android-usb-warning"
          >
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-semibold">USB / Bluetooth printers won&apos;t work in the mobile browser</div>
                <div className="text-[11px] opacity-90 mt-0.5">
                  Android browsers cannot access USB or Bluetooth devices. To print on a USB thermal printer from this tablet, install the <b>SmartoERP Android app</b> — it uses a native ESC/POS bridge that discovers plugged-in printers. Alternative: use a Wi-Fi/LAN printer (works in the browser via Print Bridge on a nearby PC).
                </div>
              </div>
            </div>
            <a
              href="/downloads/smarto-android.apk"
              download
              className="shrink-0 inline-flex items-center px-2 py-1 rounded bg-amber-800 text-white text-[10px] font-semibold hover:bg-amber-900"
              data-testid="android-app-download"
            >
              Get Android app
            </a>
          </div>
        )}
        {/* iter870 — Android APP but native plugin missing (older APK). */}
        {androidAppOldApk && (
          <div
            className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12px] border bg-rose-50 text-rose-900 border-rose-300 mb-2"
            data-testid="android-old-apk-warning"
          >
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <div className="font-semibold">USB print bridge missing from this APK</div>
              <div className="text-[11px] opacity-90 mt-0.5">
                You&apos;re in the SmartoERP Android app but this build doesn&apos;t include the native USB / Bluetooth printer bridge — that means an older APK is installed. <b>Two fixes:</b> (1) reinstall the latest APK from the landing page or from Settings → App Updates, or (2) for TM-T88V and other network-capable printers, add the printer&apos;s LAN IP address below to switch to network printing (works immediately, no APK update needed).
              </div>
            </div>
          </div>
        )}
        {!bannerDismissed && !(bridgeAlive === true && !iOSMode) && (
        <div
          className={`flex items-center justify-between gap-2 rounded-lg px-2.5 py-1 text-[11px] border ${
            iOSMode ? 'bg-sky-50/70 text-sky-800 border-sky-200'
            : bridgeAlive === false ? 'bg-amber-50/70 text-amber-800 border-amber-200'
            : 'bg-slate-50/70 text-slate-600 border-slate-200'
          }`}
          data-testid="bridge-status-banner"
        >
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            {iOSMode ? <Info size={11} className="shrink-0" />
              : bridgeAlive === false ? <XCircle size={11} className="shrink-0" />
              : <Loader2 size={11} className="animate-spin shrink-0" />}
            <span className="truncate">
              {iOSMode && 'iPad detected — silent print via Cloud Relay Bridge (PC/Mac/Android at branch).'}
              {!iOSMode && bridgeAlive === false && 'Print Bridge not running on this PC.'}
              {!iOSMode && bridgeAlive === null && 'Checking print bridge…'}
            </span>
            {(iOSMode || bridgeAlive === false) && (
              <span className="flex items-center gap-1 ml-1 flex-wrap">
                {!iOSMode && (
                  <>
                    <a
                      href="/downloads/smarto_print_bridge_installer.bat"
                      download="SmartoPrintBridge_Installer.bat"
                      className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9px] font-semibold hover:bg-emerald-700"
                      data-testid="bridge-banner-install-windows"
                    >
                      Windows
                    </a>
                    <a
                      href="/downloads/smarto_print_bridge_installer.command"
                      download="SmartoPrintBridge_Installer.command"
                      className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[9px] font-semibold hover:bg-emerald-700"
                      data-testid="bridge-banner-install-mac"
                    >
                      Mac
                    </a>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setAndroidHelpOpen(true)}
                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-lime-600 text-white text-[9px] font-semibold hover:bg-lime-700"
                  data-testid="bridge-banner-install-android"
                  title="Run the Bridge on any Android phone at the branch — perfect for iPad-only setups"
                >
                  Android
                </button>
              </span>
            )}
          </div>
          <Button
            size="icon"
            variant="ghost"
            className="h-5 w-5 p-0 hover:bg-black/5 shrink-0"
            onClick={() => {
              setBannerDismissed(true);
              try { sessionStorage.setItem('printers_bridge_banner_dismissed', '1'); } catch { /* ignore */ }
            }}
            data-testid="dismiss-bridge-banner"
            title="Dismiss"
          >
            <XCircle size={10} />
          </Button>
        </div>
        )}

        {/* iter679 — iPad printing options consolidated into a single
            Dialog. Used to be two huge inline cards (auto-iPrint + 
            ePOS-Print Direct) hogging ~260 lines of vertical space on
            every load. Now triggered by the "📱 iPad options" chip in
            the header. PC/Mac users never see this content. */}
        {/* iter693 — Extracted into <IPadOptionsDialog>. */}
        <IPadOptionsDialog
          open={ipadOptionsOpen}
          onOpenChange={setIpadOptionsOpen}
          printers={printers}
          eposTestState={eposTestState}
          setEposTestState={setEposTestState}
          setIpadVerifyState={setIpadVerifyState}
          setAcceptAllIndex={setAcceptAllIndex}
          setAcceptAllOpen={setAcceptAllOpen}
          setEposConfirmOpen={setEposConfirmOpen}
        />

        {/* iter696 — Extracted into <PrinterListView>. */}
        <PrinterListView
          printers={printers}
          branches={branches}
          loading={loading}
          pingResults={pingResults}
          pinging={pinging}
          testing={testing}
          eposRowTesting={eposRowTesting}
          bilingualTesting={bilingualTesting}
          bilingualTrends={bilingualTrends}
          onAdd={openAdd}
          onEdit={openEdit}
          onDelete={remove}
          onPing={ping}
          onTestPrint={testPrint}
          onTestEposDirect={testEposDirectRow}
          onBilingualTest={testBilingualRow}
        />
      </div>

      {/* Add/Edit sheet drawer */}
      {/* iter693 — Extracted into <PrinterEditDrawer>. */}
      <PrinterEditDrawer
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        branches={branches}
        saving={saving}
        onSave={save}
        onTestPrint={testPrint}
        testing={testing}
      />

      {/* iter844 — Printer troubleshooting quick-fixes drawer */}
      <PrinterHelpDrawer open={helpOpen} onOpenChange={setHelpOpen} />

      {/* iter652 — Bulk-import CSV dialog */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-xl" data-testid="bulk-import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={18} className="text-orange-500" />
              Bulk-import printers
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Paste one printer per line. Header row required. Match a branch by exact name or branch_id (UUID).
            </p>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              value={bulkCsv}
              onChange={(e) => setBulkCsv(e.target.value)}
              rows={9}
              placeholder={'name,type,ip_address,port,branch_name,station,paper_width\nMain Cashier,receipt,192.168.1.50,9100,Main,,80mm\nKitchen Hot Line,kitchen,192.168.1.60,9100,Main,Hot Grill,80mm\nBar Cocktails,bar,192.168.1.70,9100,Main,Cocktails,80mm'}
              className="font-mono text-[11px]"
              data-testid="bulk-import-csv-area"
            />
            <p className="text-[10px] text-muted-foreground">
              Allowed types: <code>receipt</code>, <code>kitchen</code>, <code>bar</code>, <code>label</code>. Rows with a duplicate name+branch+ip are skipped — safe to re-run.
            </p>
            {bulkResult && (
              <div className="text-[11px] space-y-1 p-3 rounded-lg bg-stone-50 border border-stone-200" data-testid="bulk-import-result">
                <div className="font-semibold text-emerald-700">✓ Created {bulkResult.created} printer(s)</div>
                {bulkResult.skipped && bulkResult.skipped.length > 0 && (
                  <div className="text-amber-700">
                    Skipped {bulkResult.skipped.length} — first 3: {bulkResult.skipped.slice(0, 3).join('; ')}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setBulkOpen(false)} data-testid="bulk-import-cancel">Close</Button>
            <Button
              size="sm"
              disabled={bulkRunning || !bulkCsv.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={async () => {
                setBulkRunning(true);
                setBulkResult(null);
                try {
                  const { data } = await api.post('/cashier/printers/bulk-import', { csv: bulkCsv });
                  setBulkResult(data);
                  toast.success(`Imported ${data.created} printer(s)`);
                  await reload();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Bulk import failed');
                } finally {
                  setBulkRunning(false);
                }
              }}
              data-testid="bulk-import-run-btn"
            >
              {bulkRunning ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Upload size={14} className="mr-1.5" />}
              Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter653 — Scan LAN dialog (mDNS + TCP-9100 via local Print Bridge)
          iter658 — On iPad we swap to a Quick-Add-by-IP form (no LAN scan
          possible from Safari) so users can register their TM-m30II in
          ~10 seconds straight from the tablet. */}
      {/* iter695 — Extracted into <EposDirectPanel>. */}
      <EposDirectPanel
        open={scanOpen}
        onOpenChange={setScanOpen}
        iOSMode={iOSMode}
        ipadVerifyState={ipadVerifyState}
        setIpadVerifyState={setIpadVerifyState}
        onPrefillFromScan={(ip, port, name) => {
          // When the user clicks "Use" on a scanned printer, populate the
          // Add drawer and switch to it.
          setEditing(null);
          setForm({ ...EMPTY_FORM, name, ip_address: ip, port });
          setScanOpen(false);
          setSheetOpen(true);
        }}
        onPrinterAdded={reload}
      />

      {/* iter871 — Auto-discover results dialog. Renders whatever the Android
          native plugin OR the Print Bridge returned; one-tap prefills the
          Add drawer with the chosen IP/port. */}
      <Dialog open={autoDiscoverOpen} onOpenChange={setAutoDiscoverOpen}>
        <DialogContent className="max-w-lg" data-testid="auto-discover-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Radar size={18} className="text-teal-600" />
              Auto-discover results
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {autoDiscoverResults && (
              <p className="text-xs text-muted-foreground">
                Scanned via <b>{autoDiscoverResults.source === 'android_plugin' ? 'SmartoERP Android app' : 'Print Bridge'}</b>
                {autoDiscoverResults.subnet && <> on subnet <code className="font-mono bg-muted px-1 rounded">{autoDiscoverResults.subnet}</code></>}
                {autoDiscoverResults.error && <span className="text-rose-600"> — {autoDiscoverResults.error}</span>}
              </p>
            )}
            {(autoDiscoverResults?.printers?.length || 0) === 0 ? (
              <div className="p-4 rounded-lg bg-muted text-sm text-muted-foreground text-center" data-testid="auto-discover-empty">
                No ESC/POS printers on TCP-9100 found. Make sure the printer is powered on, plugged into Wi-Fi/Ethernet, and on the same network as this device.
              </div>
            ) : (
              <div className="border rounded-lg divide-y max-h-80 overflow-y-auto" data-testid="auto-discover-list">
                {autoDiscoverResults.printers.map((p) => {
                  const ip = p.ip || p.address || p.host;
                  const port = p.port || 9100;
                  const label = p.name || p.model || `Printer @ ${ip}`;
                  return (
                    <div key={`${ip}:${port}`} className="flex items-center justify-between gap-3 p-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{label}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{ip}:{port}</div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditing(null);
                          setForm({ ...EMPTY_FORM,
                            name: label.length > 40 ? label.slice(0, 40) : label,
                            ip_address: ip,
                            port,
                            connection_type: 'network',
                          });
                          setAutoDiscoverOpen(false);
                          setSheetOpen(true);
                        }}
                        data-testid={`auto-discover-use-${ip.replace(/\./g, '-')}`}
                      >
                        <Plus size={12} className="mr-1" /> Use
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="text-[11px] text-muted-foreground">
              Not seeing your printer? It might not be on TCP-9100 or on this Wi-Fi. Use <b>Add new printer</b> to enter the IP manually.
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* iter664 — Android (Termux) Bridge install help. Lets iPad-only
          branches turn an existing Android phone into the Print Bridge.
          Shows a copy-paste curl one-liner + the branch's relay token. */}
      <Dialog
        open={androidHelpOpen}
        onOpenChange={(open) => {
          setAndroidHelpOpen(open);
          // Lazy-fetch the relay token only when the dialog is opened.
          if (open && relayToken === null && !relayTokenLoading) {
            loadRelayToken();
          }
        }}
      >
        <DialogContent className="max-w-2xl" data-testid="android-bridge-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-lime-600">🤖</span>
              Install Bridge on a branch Android phone (Termux)
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Turn any existing Android phone into a Print Bridge for this branch&apos;s iPads.
              No PC, no Lantronix box, no extra hardware. Works with every thermal model
              (TM-m30II, TM-T88VI, Sunmi NT310, etc.) on TCP-9100.
            </p>
          </DialogHeader>

          <div className="space-y-4 py-2 text-sm">
            <div className="p-3 rounded-lg bg-sky-50 border border-sky-200">
              <p className="font-semibold text-sky-900 mb-1.5">📲 Step 1 — Install Termux on the Android phone</p>
              <ol className="list-decimal ml-5 space-y-1 text-sky-900 text-xs">
                <li>On the Android phone, open this link in Chrome: <code className="bg-white px-1 rounded">f-droid.org/en/packages/com.termux/</code></li>
                <li>Tap <strong>Download APK</strong> → install it (you may need to enable &ldquo;Install from unknown sources&rdquo;).</li>
                <li>(Recommended) Also install <strong>Termux:Boot</strong> from <code className="bg-white px-1 rounded">f-droid.org/en/packages/com.termux.boot/</code> so the Bridge auto-starts on reboot.</li>
              </ol>
              <p className="text-[11px] text-sky-800 mt-2">⚠ Do NOT install Termux from Google Play — that version is outdated and broken. Use F-Droid only.</p>
            </div>

            <div className="p-3 rounded-lg bg-lime-50 border border-lime-200">
              <p className="font-semibold text-lime-900 mb-1.5">⚡ Step 2 — Run the installer (one command)</p>
              <p className="text-xs text-lime-900 mb-2">Open Termux on the Android phone and paste this command:</p>
              <div className="relative">
                <pre className="bg-zinc-900 text-lime-200 text-[11px] font-mono p-2.5 rounded overflow-x-auto whitespace-pre-wrap break-all" data-testid="android-install-oneliner">
{`curl -fsSL https://smartoerp.com/downloads/smarto_bridge_android_install.sh | bash`}
                </pre>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText('curl -fsSL https://smartoerp.com/downloads/smarto_bridge_android_install.sh | bash');
                    toast.success('Command copied to clipboard');
                  }}
                  className="absolute top-1.5 right-1.5 text-[10px] px-2 py-0.5 rounded bg-lime-600 hover:bg-lime-700 text-white font-semibold"
                  data-testid="android-install-copy"
                >
                  Copy
                </button>
              </div>
              <p className="text-[11px] text-lime-800 mt-2">The installer will ask for your Branch Bridge Relay Token (next step).</p>
            </div>

            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="font-semibold text-amber-900 mb-1.5">🔑 Step 3 — Branch Bridge Relay Token</p>
              <p className="text-xs text-amber-900 mb-2">When the installer asks, paste this token:</p>
              <div className="flex items-stretch gap-1.5">
                <input
                  type="text"
                  readOnly
                  value={relayTokenLoading ? 'Loading…' : (relayToken || (relayTokenError ? `Failed: ${relayTokenError}` : '(tap Retry)'))}
                  className="flex-1 h-9 px-3 rounded-md border border-amber-300 bg-white text-sm font-mono"
                  data-testid="android-relay-token"
                />
                {!relayToken && !relayTokenLoading && (
                  <button
                    type="button"
                    onClick={loadRelayToken}
                    className="px-3 h-9 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold"
                    data-testid="android-relay-token-retry"
                  >
                    Retry
                  </button>
                )}
                <button
                  type="button"
                  disabled={!relayToken}
                  onClick={() => {
                    navigator.clipboard?.writeText(relayToken || '');
                    toast.success('Token copied to clipboard');
                  }}
                  className="h-9 px-3 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold disabled:opacity-50"
                  data-testid="android-relay-token-copy"
                >
                  Copy
                </button>
              </div>
              <p className="text-[11px] text-amber-800 mt-2">
                ⚠ This token is the same for every branch on your tenant — anyone with it can act as a Bridge.
                Treat it like a password. If it leaks, contact support to rotate.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-zinc-50 border border-zinc-200">
              <p className="font-semibold text-zinc-900 mb-1.5">🔋 Step 4 — Keep it running 24/7</p>
              <ul className="list-disc ml-5 space-y-0.5 text-zinc-700 text-xs">
                <li>Plug the Android phone into power permanently.</li>
                <li>Connect it to the same Wi-Fi as the printers and the iPad.</li>
                <li>Phone Settings → Battery → Battery optimisation → Termux → <strong>Don&apos;t optimise</strong>.</li>
                <li>Don&apos;t force-close the Termux app. Pull-up gestures are fine.</li>
                <li>Verify on this page: a green <strong>✓ Bridge active</strong> badge will appear on the printer row within 10s of starting.</li>
              </ul>
            </div>

            {/* iter665 — Test Bridge live check. After running the installer
                on the phone, tap this to confirm a Bridge has actually
                registered + is polling our cloud. Removes guesswork. */}
            <div className="p-3 rounded-lg bg-violet-50 border border-violet-200">
              <p className="font-semibold text-violet-900 mb-1.5">🧪 Step 5 — Test the Bridge</p>
              <p className="text-xs text-violet-900 mb-2">
                After you&apos;ve run the installer on the Android phone, tap this to confirm the phone is talking to our cloud.
                We&apos;ll list every bridge that polled in the last 60 seconds.
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={bridgeTest.loading}
                  className="h-9 rounded-md bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold"
                  onClick={async () => {
                    setBridgeTest({ loading: true, result: null });
                    try {
                      const res = await api.get('/cashier/print-bridge/bridges');
                      const bridges = res.data?.bridges || [];
                      const online = bridges.filter((b) => b.online);
                      setBridgeTest({
                        loading: false,
                        result: { online, total: bridges.length, ts: new Date() },
                      });
                    } catch (e) {
                      setBridgeTest({
                        loading: false,
                        result: { error: e?.response?.data?.detail || 'Could not reach the cloud relay.' },
                      });
                    }
                  }}
                  data-testid="android-test-bridge-btn"
                >
                  {bridgeTest.loading ? <><Loader2 size={13} className="animate-spin mr-1" />Probing…</> : '🧪 Test Bridge now'}
                </Button>
                {bridgeTest.result?.online?.length > 0 && (
                  <span className="text-xs text-emerald-700 font-semibold" data-testid="android-test-bridge-result-ok">
                    ✓ {bridgeTest.result.online.length} bridge{bridgeTest.result.online.length === 1 ? '' : 's'} online
                  </span>
                )}
                {bridgeTest.result && !bridgeTest.result.error && bridgeTest.result.online?.length === 0 && (
                  <span className="text-xs text-rose-700 font-semibold" data-testid="android-test-bridge-result-fail">
                    ✗ No bridge polled in the last 60s
                  </span>
                )}
              </div>
              {bridgeTest.result?.online?.length > 0 && (
                <div className="mt-2 space-y-1">
                  {bridgeTest.result.online.map((b) => (
                    <div key={b.id} className="text-[11px] font-mono bg-white border border-emerald-200 rounded px-2 py-1 text-emerald-900">
                      ✓ <strong>{b.name || b.id}</strong> · v{b.version || '?'} · last seen just now
                    </div>
                  ))}
                </div>
              )}
              {bridgeTest.result && !bridgeTest.result.error && bridgeTest.result.online?.length === 0 && (
                <div className="mt-2 text-[11px] text-rose-900 bg-white border border-rose-200 rounded px-2 py-1.5 space-y-0.5">
                  <p>Common causes:</p>
                  <ul className="list-disc ml-4">
                    <li>Termux app was closed/killed → re-open Termux on the phone and run <code className="bg-zinc-100 px-1 rounded">~/smarto-bridge/run-bridge.sh</code></li>
                    <li>Phone is on a different Wi-Fi than the iPad → check &amp; reconnect</li>
                    <li>Battery optimisation killed Python → disable for Termux</li>
                    <li>Wrong relay token pasted during install → re-run the installer</li>
                  </ul>
                </div>
              )}
              {bridgeTest.result?.error && (
                <p className="mt-2 text-[11px] text-rose-700">{bridgeTest.result.error}</p>
              )}
            </div>

            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
              <p className="font-semibold mb-1">✓ After Bridge is running</p>
              <p>
                Every iPad at this branch will print silently via:
                <span className="block font-mono text-[11px] mt-1 text-emerald-800">iPad → smartoerp.com cloud → Android Bridge → Printer 🧾</span>
              </p>
              <p className="mt-1.5">
                Works with all thermal printers on TCP-9100 — TM-m30II, TM-T88VI, Sunmi NT310, mixed models in the same branch, no problem.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setAndroidHelpOpen(false)} data-testid="android-bridge-close">
              Close
            </Button>
            <a
              href="/downloads/smarto_bridge_android_install.sh"
              download="smarto_bridge_android_install.sh"
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-lime-600 hover:bg-lime-700 text-white text-xs font-semibold"
              data-testid="android-bridge-download"
            >
              <Upload size={13} className="rotate-180" />
              Download installer.sh
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter670 — "Did the slip print?" confirmation modal. ePOS-Direct
          uses no-cors fetch so we can't read the printer's response; the
          only ground-truth signal is the cashier eyeballing the slip.
          A YES auto-flips the toggle ON; a NO surfaces troubleshooting. */}
      <Dialog open={eposConfirmOpen} onOpenChange={setEposConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="epos-direct-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">🖨️</span>
              Did the test slip print?
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              We sent a fire-and-forget request to the printer&apos;s ePOS-Print HTTP service.
              Because the browser can&apos;t read the printer&apos;s response, we need you to look
              at the actual paper to confirm it worked.
            </p>
          </DialogHeader>
          <div className="py-3 space-y-3">
            <div className="p-3 rounded-lg bg-emerald-50 border-2 border-emerald-300 text-xs text-emerald-900">
              <p className="font-bold mb-1">✓ YES — the slip printed</p>
              <p>I&apos;ll auto-flip the ePOS-Direct toggle <strong>ON</strong> so every payment auto-prints silently from now on.</p>
            </div>
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-900">
              <p className="font-bold mb-1">✗ NO — no slip came out</p>
              <p>Most common cause: Safari hasn&apos;t accepted the printer&apos;s self-signed cert yet.</p>
              <p className="mt-1">Tap <strong>&ldquo;🔐 Accept printer cert&rdquo;</strong> first, then re-run the Test.</p>
            </div>
          </div>
          <DialogFooter className="flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50"
              onClick={() => {
                setEposConfirmOpen(false);
                toast.info('Toggle left OFF. Try accepting the cert first.');
              }}
              data-testid="epos-direct-confirm-no"
            >
              ✗ No
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => {
                localStorage.setItem('epos_direct_on_pay', '1');
                setEposConfirmOpen(false);
                // Re-render so the toggle visual flips.
                setEposTestState((s) => ({ ...s }));
                toast.success('ePOS-Direct enabled — every payment now prints silently!');
              }}
              data-testid="epos-direct-confirm-yes"
            >
              ✓ Yes — enable auto-print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter672 — Mass QR Setup Wizard.
          One QR per branch, encoding the deep-link URL that auto-scrolls
          + highlights the ePOS-Direct panel on the cashier's iPad/Tab. */}
      {/* iter683 — Receipt preview dialog. Lets operator verify the
          slip layout (header/footer/logo) without burning paper. */}
      <ReceiptPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        defaultPaperMm={80}
      />

      {/* iter690 — POS Terminal branch dialog. Writes the picked
          branch_id to localStorage so the next cashier PIN login from
          this device sends it to the server, anchoring all sales made
          at this terminal to the right branch regardless of cashier
          home branch. */}
      <PosTerminalBranchDialog
        open={terminalBranchOpen}
        onOpenChange={_handleTerminalOpenChange}
        branches={branches || []}
      />

      <Dialog open={massQrOpen} onOpenChange={setMassQrOpen}>
        <DialogContent className="max-w-3xl max-h-[85dvh] overflow-y-auto" data-testid="mass-qr-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>📱</span> Mass setup — one QR per branch
            </DialogTitle>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Print these QR codes (or just show them on this screen). At each branch,
              the cashier scans the QR on their iPad / Samsung Tab → lands on the
              guided ePOS-Direct setup with the printer&apos;s IP pre-filled →
              completes setup in ~3 taps. Cuts your 10-branch rollout to ~5 minutes.
            </p>
          </DialogHeader>

          <div className="py-3 space-y-3">
            {branches.length === 0 && (
              <div className="text-xs text-muted-foreground p-3 border border-dashed rounded">
                No branches found. Add branches first under Settings → Branches.
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {branches.map((branch) => {
                // Find the receipt printer for this branch (branch-specific first,
                // then fall back to the "all branches" shared printer).
                const branchPrinter = printers.find(p => p.branch_id === branch.id && (p.type || 'receipt') === 'receipt')
                  || printers.find(p => !p.branch_id && (p.type || 'receipt') === 'receipt');
                const ip = branchPrinter?.ip_address || '';
                const base = window.location.origin;
                const setupUrl = ip
                  ? `${base}/settings/printers?wizard=epos&printer_ip=${encodeURIComponent(ip)}&branch=${encodeURIComponent(branch.id)}`
                  : `${base}/settings/printers?wizard=epos&branch=${encodeURIComponent(branch.id)}`;
                return (
                  <div key={branch.id} className="border-2 border-emerald-200 bg-emerald-50/50 rounded-lg p-3 flex flex-col items-center gap-2" data-testid={`mass-qr-card-${branch.id}`}>
                    <p className="font-bold text-sm text-emerald-900">{branch.name}</p>
                    {ip ? (
                      <p className="text-[10px] font-mono text-emerald-800">{ip}</p>
                    ) : (
                      <p className="text-[10px] text-rose-700">⚠ No printer configured</p>
                    )}
                    <div className="bg-white p-2 rounded-md border border-emerald-300">
                      <QRCodeSVG value={setupUrl} size={140} level="M" />
                    </div>
                    <div className="flex items-center gap-1.5 w-full">
                      <button
                        type="button"
                        className="flex-1 text-[10px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                        onClick={() => {
                          navigator.clipboard?.writeText(setupUrl);
                          toast.success(`URL copied for ${branch.name}`);
                        }}
                      >
                        Copy URL
                      </button>
                      <button
                        type="button"
                        className="flex-1 text-[10px] text-emerald-700 hover:text-emerald-900 underline underline-offset-2"
                        onClick={() => window.open(setupUrl, '_blank', 'noopener,noreferrer')}
                      >
                        Open in new tab
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs text-sky-900 space-y-1">
              <p className="font-semibold">📋 How to use:</p>
              <ol className="list-decimal ml-5 space-y-0.5">
                <li>On any device with a camera: print this page, OR show this dialog on a screen.</li>
                <li>At each branch: the cashier opens the iPad/Samsung Tab Camera and scans the branch&apos;s QR.</li>
                <li>The tablet opens smartoerp.com → auto-scrolls to the ePOS-Direct panel → pulses it.</li>
                <li>Cashier taps <strong>🔐 Accept printer cert</strong> → accepts in Safari/Chrome.</li>
                <li>Taps <strong>🧪 Test ePOS Direct</strong> → if slip prints, taps <strong>✓ Yes</strong>. Done!</li>
              </ol>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setMassQrOpen(false)} data-testid="mass-qr-close">
              Close
            </Button>
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => window.print()} /* eslint-print-native-ok — mass QR printout, browser dialog is intended */
              data-testid="mass-qr-print"
            >
              <Printer size={13} className="mr-1.5" />
              Print this page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter676 — Accept-ALL printer certs guided wizard.
          Walks the cashier through each IP printer one at a time so the
          popup blocker doesn't swallow batch window.open() calls. */}
      <Dialog open={acceptAllOpen} onOpenChange={setAcceptAllOpen}>
        <DialogContent className="max-w-md" data-testid="accept-all-certs-dialog">
          {(() => {
            const ipPrinters = (printers || []).filter(p => p.ip_address);
            const total = ipPrinters.length;
            const current = ipPrinters[acceptAllIndex];
            const isDone = acceptAllIndex >= total;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <span>🔐</span>
                    {isDone ? 'All certs accepted!' : `Accept printer cert (${acceptAllIndex + 1} of ${total})`}
                  </DialogTitle>
                  <p className="text-xs text-muted-foreground">
                    {isDone
                      ? 'Every printer at this branch is now trusted by Safari. ePOS-Direct will print silently to all of them.'
                      : 'Tap the green button to open this printer\u2019s admin in a new tab. Tap Show Details → Visit this website → Visit Website. Then return here and tap Next.'}
                  </p>
                </DialogHeader>
                <div className="py-3 space-y-3">
                  {!isDone && current && (
                    <div className="p-3 rounded-lg bg-emerald-50 border-2 border-emerald-300">
                      <p className="font-bold text-emerald-900 text-sm">{current.name}</p>
                      <p className="text-[11px] font-mono text-emerald-800">{current.ip_address}</p>
                      <p className="text-[10px] text-emerald-700 mt-1">
                        Role: {current.type || 'receipt'}{current.station ? ` · ${current.station}` : ''}
                      </p>
                      <Button
                        type="button"
                        size="sm"
                        className="mt-2 w-full h-8 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                        onClick={() => openPrinterCertPage(current.ip_address)}
                        data-testid="accept-all-open-current"
                      >
                        🔗 Open {current.name} admin
                      </Button>
                    </div>
                  )}
                  {isDone && (
                    <div className="p-3 rounded-lg bg-emerald-50 border-2 border-emerald-300 text-center">
                      <p className="text-4xl mb-2">✅</p>
                      <p className="font-bold text-emerald-900">{total} printer{total === 1 ? '' : 's'} ready</p>
                      <p className="text-[11px] text-emerald-800 mt-1">
                        Now complete a real order — receipts AND kitchen tickets will print silently.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-1.5">
                    {ipPrinters.map((_, i) => (
                      <div
                        key={i}
                        className={`h-1.5 w-6 rounded-full ${i < acceptAllIndex ? 'bg-emerald-600' : i === acceptAllIndex ? 'bg-emerald-300' : 'bg-zinc-200'}`}
                      />
                    ))}
                  </div>
                </div>
                <DialogFooter className="flex-row gap-2">
                  {!isDone ? (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAcceptAllOpen(false)}
                        data-testid="accept-all-skip"
                      >
                        Skip
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => setAcceptAllIndex(acceptAllIndex + 1)}
                        data-testid="accept-all-next"
                      >
                        ✓ Done — next printer →
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => {
                        setAcceptAllOpen(false);
                        setAcceptAllIndex(0);
                        toast.success('All printers trusted. Complete an order to test.');
                      }}
                      data-testid="accept-all-finish"
                    >
                      Close
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
      {/* iter657 — Guided Epson TM-m30II setup wizard (no PC, no Lantronix, just iPad). */}
      <Dialog open={tmWizardOpen} onOpenChange={setTmWizardOpen}>
        <DialogContent className="max-w-lg" data-testid="tm-m30ii-wizard-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <span className="text-xl">🍎</span>
              Setup Epson TM-m30II for iPad
              <Badge className="text-[9px] bg-emerald-100 text-emerald-800 border border-emerald-300">No PC needed</Badge>
            </DialogTitle>
            <p className="text-xs text-muted-foreground">
              Your TM-m30II has AirPrint built in. Follow these 4 steps to print receipts straight from iPad — no Bridge, no Lantronix box, no Windows PC.
            </p>
          </DialogHeader>

          <div className="py-2 space-y-3" data-testid={`tm-wizard-step-${tmWizardStep}`}>
            {/* iter658 — iPad fast path: skip steps 1-3 because Safari does its own
                AirPrint discovery in the system print dialog. We just collect a name. */}
            {tmWizardMode === 'ipad-fast' && (
              <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-300 text-xs" data-testid="tm-ipad-fast-banner">
                <div className="flex items-start gap-2">
                  <span className="text-base shrink-0">⚡</span>
                  <div>
                    <p className="font-semibold text-emerald-900">iPad detected — fast path enabled</p>
                    <p className="text-emerald-800 mt-0.5">
                      Safari already discovers AirPrint printers automatically. You don&apos;t need an IP address. Just give the printer a name and tap Create. When you print, iPad shows ALL nearby AirPrint printers in a list — pick the TM-m30II from there.
                    </p>
                    <button
                      type="button"
                      className="text-[10px] underline mt-1 text-emerald-700"
                      onClick={() => { setTmWizardMode('full'); setTmWizardStep(0); }}
                      data-testid="tm-switch-full-mode"
                    >
                      Or: use the full 4-step setup instead →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {tmWizardMode === 'ipad-fast' ? (
              <div className="space-y-2 text-sm" data-testid="tm-ipad-fast-form">
                <div>
                  <Label className="text-xs">Printer name</Label>
                  <Input
                    value={tmWizardForm.name}
                    onChange={(e) => setTmWizardForm({ ...tmWizardForm, name: e.target.value })}
                    className="mt-1 h-9 rounded-lg"
                    data-testid="tm-wizard-fast-name"
                  />
                </div>
                <div>
                  <Label className="text-xs">Branch</Label>
                  <Select
                    value={tmWizardForm.branch_id || '__all__'}
                    onValueChange={(v) => setTmWizardForm({ ...tmWizardForm, branch_id: v === '__all__' ? null : v })}
                  >
                    <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="tm-wizard-fast-branch">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">All branches (shared)</SelectItem>
                      {branches.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="p-2 mt-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900">
                  <strong>Created as:</strong> 80mm receipt · <strong>🍎 AirPrint enabled</strong> · default · auto-print. Test print opens Safari&apos;s native dialog so you can pick from any AirPrint printer on the network.
                </div>
              </div>
            ) : (
              <>
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= tmWizardStep ? 'bg-emerald-500' : 'bg-stone-200'}`} />
              ))}
            </div>
            <p className="text-[10px] text-muted-foreground text-center">Step {tmWizardStep + 1} of 4</p>

            {tmWizardStep === 0 && (
              <div className="space-y-2 text-sm" data-testid="tm-step-network">
                <h3 className="font-bold flex items-center gap-2"><Wifi size={14} className="text-emerald-600" /> 1. Connect to your network</h3>
                <p className="text-xs text-muted-foreground">Pick the easiest path:</p>
                <ul className="text-xs space-y-1 ml-4 list-disc">
                  <li><strong>Ethernet (fastest):</strong> plug an Ethernet cable from the printer&apos;s <code>LAN</code> port into a free port on your Wi-Fi router. Done.</li>
                  <li><strong>Wi-Fi (if no Ethernet):</strong> install the free <strong>Epson TM Utility</strong> app from the iPad App Store → tap your printer → Network Settings → join Wi-Fi.</li>
                </ul>
                <p className="text-[11px] text-muted-foreground italic">The printer&apos;s power light should be steady green when connected.</p>
              </div>
            )}

            {tmWizardStep === 1 && (
              <div className="space-y-2 text-sm" data-testid="tm-step-ip">
                <h3 className="font-bold flex items-center gap-2"><Info size={14} className="text-emerald-600" /> 2. Get the printer&apos;s IP address</h3>
                <ol className="text-xs space-y-1.5 ml-4 list-decimal">
                  <li>Turn the printer <strong>OFF</strong>.</li>
                  <li>Hold the <strong>FEED button</strong> (small button on top of the printer).</li>
                  <li>While holding FEED, turn the printer <strong>ON</strong>.</li>
                  <li>A self-test receipt prints. Look for <code>IP Address: 192.168.x.xxx</code>.</li>
                </ol>
                <div className="mt-2">
                  <Label className="text-xs">Type the IP address here:</Label>
                  <Input
                    placeholder="e.g., 192.168.1.50"
                    value={tmWizardForm.ip_address}
                    onChange={(e) => setTmWizardForm({ ...tmWizardForm, ip_address: e.target.value.trim() })}
                    className="mt-1 h-9 rounded-lg font-mono"
                    data-testid="tm-wizard-ip-input"
                  />
                </div>
              </div>
            )}

            {tmWizardStep === 2 && (
              <div className="space-y-2 text-sm" data-testid="tm-step-airprint">
                <h3 className="font-bold flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-600" /> 3. Verify iPad can see the printer</h3>
                <p className="text-xs text-muted-foreground"><strong>Do this BEFORE finishing in Smarto</strong> — it confirms AirPrint is working:</p>
                <ol className="text-xs space-y-1.5 ml-4 list-decimal">
                  <li>Open <strong>Safari</strong> on this iPad.</li>
                  <li>Go to any web page (e.g., google.com).</li>
                  <li>Tap the <strong>Share</strong> button (square with up arrow).</li>
                  <li>Scroll down → tap <strong>Print</strong>.</li>
                  <li>Tap <strong>&quot;Select Printer&quot;</strong>.</li>
                  <li>✅ Your <strong>TM-m30II</strong> should appear in the list. Tap Cancel — no need to actually print here.</li>
                </ol>
                <details className="text-xs pt-1">
                  <summary className="cursor-pointer text-rose-700 font-semibold">❌ Printer NOT in the list? Tap to fix.</summary>
                  <div className="mt-2 p-2 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="font-semibold mb-1">Enable AirPrint in printer firmware:</p>
                    <ol className="space-y-0.5 ml-4 list-decimal">
                      <li>On iPad Safari, go to <code>http://{tmWizardForm.ip_address || '<your-printer-ip>'}/</code></li>
                      <li>Login (default: user <code>epson</code> / password <code>epson</code>).</li>
                      <li>Find <strong>Network → Bonjour / AirPrint</strong>.</li>
                      <li>Make sure both <strong>Bonjour</strong> and <strong>AirPrint</strong> are <strong>ENABLED</strong>.</li>
                      <li>Save → reboot printer → retry Step 3.</li>
                    </ol>
                  </div>
                </details>
              </div>
            )}

            {tmWizardStep === 3 && (
              <div className="space-y-2 text-sm" data-testid="tm-step-create">
                <h3 className="font-bold flex items-center gap-2"><Printer size={14} className="text-emerald-600" /> 4. Create the printer in Smarto</h3>
                <p className="text-xs text-muted-foreground">I&apos;ll create it for you with AirPrint enabled. Just confirm the details:</p>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs">Printer name</Label>
                    <Input
                      value={tmWizardForm.name}
                      onChange={(e) => setTmWizardForm({ ...tmWizardForm, name: e.target.value })}
                      className="mt-1 h-9 rounded-lg"
                      data-testid="tm-wizard-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">IP address (from Step 2)</Label>
                    <Input
                      value={tmWizardForm.ip_address}
                      onChange={(e) => setTmWizardForm({ ...tmWizardForm, ip_address: e.target.value })}
                      className="mt-1 h-9 rounded-lg font-mono"
                      data-testid="tm-wizard-ip-confirm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Branch</Label>
                    <Select
                      value={tmWizardForm.branch_id || '__all__'}
                      onValueChange={(v) => setTmWizardForm({ ...tmWizardForm, branch_id: v === '__all__' ? null : v })}
                    >
                      <SelectTrigger className="mt-1 h-9 rounded-lg" data-testid="tm-wizard-branch">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All branches (shared)</SelectItem>
                        {branches.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="p-2 mt-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[11px] text-emerald-900">
                  <strong>Will be created as:</strong> Receipt printer · 80mm paper · Port 9100 · <strong>🍎 AirPrint enabled</strong> · Default for this branch · Auto-print on checkout
                </div>
              </div>
            )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost" size="sm"
              onClick={() => {
                if (tmWizardMode === 'ipad-fast' || tmWizardStep === 0) setTmWizardOpen(false);
                else setTmWizardStep(s => s - 1);
              }}
              data-testid="tm-wizard-back-btn"
            >
              {(tmWizardMode === 'ipad-fast' || tmWizardStep === 0) ? 'Cancel' : 'Back'}
            </Button>
            {tmWizardMode === 'full' && tmWizardStep < 3 ? (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={tmWizardStep === 1 && !tmWizardForm.ip_address}
                onClick={() => setTmWizardStep(s => s + 1)}
                data-testid="tm-wizard-next-btn"
              >
                Next →
              </Button>
            ) : (
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                disabled={tmWizardCreating || !tmWizardForm.name.trim() || (tmWizardMode === 'full' && !tmWizardForm.ip_address)}
                onClick={async () => {
                  setTmWizardCreating(true);
                  try {
                    await api.post('/cashier/printers', {
                      name: tmWizardForm.name.trim(),
                      type: 'receipt',
                      // iter658 — IP optional on iPad fast path; AirPrint discovery
                      // happens at print time via Safari's native picker.
                      ip_address: tmWizardForm.ip_address || '',
                      port: 9100,
                      paper_width: '80mm',
                      is_default: true,
                      auto_print: true,
                      copies: 1,
                      branch_id: tmWizardForm.branch_id,
                      print_mode: 'auto',
                      airprint_enabled: true,
                    });
                    toast.success(
                      tmWizardMode === 'ipad-fast'
                        ? 'Printer added! Tap the test icon to open Safari\'s print dialog.'
                        : 'TM-m30II added! Tap the test icon on its row to print.'
                    );
                    setTmWizardOpen(false);
                    setTmWizardStep(0);
                    setTmWizardForm({ name: 'Cashier - TM-m30II', ip_address: '', branch_id: null });
                    await reload();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to add printer');
                  } finally {
                    setTmWizardCreating(false);
                  }
                }}
                data-testid="tm-wizard-create-btn"
              >
                {tmWizardCreating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                Create printer
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
